<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { useForm } from 'vee-validate';
  import { ref, watch } from 'vue';
  import * as zod from 'zod';

  import type { Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/components/dialog';
  import { FormControl, FormField, FormItem, FormLabel } from '@ui/components/form';
  import { Input } from '@ui/components/input';
  import { toast } from '@ui/components/sonner';
  import { cn } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import { PhoneInput } from '@/components/phone-input';

  export interface AccountContactEditorProps {
    partner: Partner;
  }

  const props = defineProps<AccountContactEditorProps>();
  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'saved', partner: Partner): void;
  }>();

  const formState = ref<{
    didValidate: boolean;
    errors: Record<string, string> | null;
  }>({
    didValidate: false,
    errors: null,
  });

  const isOpen = defineModel<boolean>();

  interface FormSchema {
    email: string | undefined | null;
    name: string | undefined;
    phone: string | null;
  }

  const zodObj: Record<keyof FormSchema, zod.ZodType> = {
    email:
      props.partner.email && props.partner.linked_integration?.integration_name !== 'file-sync'
        ? zod.string().email().min(1).trim()
        : zod.union([zod.literal(''), zod.string().email().trim().nullish()]),
    name: props.partner.name ? zod.string().min(1).trim() : zod.string().trim().optional(),
    phone: zod.string().nullable(),
  };

  const form = useForm<FormSchema>({
    initialValues: {
      email: props.partner.email ?? undefined,
      name: props.partner.name ?? undefined,
      phone: props.partner.phone,
    },
    validationSchema: toTypedSchema(zod.object(zodObj)),
  });

  const isSubmitting = ref(false);
  const errorMessage = ref<string>();

  const onSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting.value) return;
    isSubmitting.value = true;

    return form
      .validate()
      .then(async (result) => {
        if (!(result.valid && result.values)) {
          formState.value.didValidate = true;
          formState.value.errors = result.errors;
          return;
        }

        const values = result.values;
        if (values.email === '') {
          values.email = null;
        }

        const partner = await stuut.partners.update(
          props.partner.id,
          { data: values },
          props.partner,
        );

        emits('saved', partner);
        return partner;
      })
      .catch((err) => {
        toast.error(err.clientMessage);
      })
      .finally(() => (isSubmitting.value = false));
  };

  watch(isOpen, () => {
    if (!isOpen.value) emits('close');
  });
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-w-[450px] w-full overflow-y-auto p-6"
      :disable-dismiss-on="['esc', 'interact-outside']"
    >
      <DialogHeader>
        <DialogTitle> Update Customer</DialogTitle>
      </DialogHeader>

      <form class="grid gap-6 relative" @submit="onSubmit">
        <p v-if="errorMessage" class="text-sm text-center text-destructive">
          {{ errorMessage }}
        </p>

        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel> Name</FormLabel>

            <FormControl>
              <Input
                type="text"
                v-bind="componentField"
                :disabled="isSubmitting"
                placeholder="Jane"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          :validate-on-input="false"
          :disabled="isSubmitting"
          name="email"
        >
          <FormItem>
            <FormLabel>Email</FormLabel>

            <FormControl>
              <Input
                type="email"
                v-bind="componentField"
                :disabled="isSubmitting"
                placeholder="name@example.com"
                auto-capitalize="none"
                auto-complete="email"
                auto-correct="off"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ value }"
          :validate-on-input="false"
          :disabled="isSubmitting"
          name="phone"
        >
          <FormItem>
            <FormLabel>Phone Number</FormLabel>

            <FormControl>
              <PhoneInput
                :disabled="isSubmitting"
                :default-value="value"
                @change="
                  (payload) => {
                    form.setFieldValue(
                      'phone',
                      payload.phoneNumber?.format('INTERNATIONAL') ?? null,
                    );
                  }
                "
              />
            </FormControl>
          </FormItem>
        </FormField>

        <div
          :class="
            cn(
              'flex flex-col gap-1',
              partner.linked_integration.integration_name === 'file-sync'
                ? 'text-destructive'
                : 'text-muted-foreground',
            )
          "
        >
          <template v-if="partner.linked_integration.integration_name === 'file-sync'">
            <span> Warning: The customer information is derived from a File Sync. </span>
            <span
              >If you make changes here, make sure to update the remote file to avoid overwriting
              during next sync.
            </span>
          </template>
          <template v-else>
            <span>
              Note: The customer information is derived from
              <strong> {{ partner.linked_integration.integration.display_name }} </strong>.
            </span>
            <span
              >Updating the customer in stuut will also update the customer in
              <strong>{{ partner.linked_integration.integration.display_name }} </strong>.
            </span>
          </template>
        </div>

        <div class="flex flex-row gap-x-4">
          <Button
            class="flex-1 hover:bg-background-muted"
            type="button"
            variant="outline"
            :disabled="isSubmitting"
            @click="emits('close')"
          >
            Cancel
          </Button>

          <Button class="flex-1" type="submit" :loading="isSubmitting"> Save </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
